/**
 * Reusable event-detail modal — used in MyCalendarPage and MyCalendarWidget.
 * Handles: activity / task / CRM audit / travel booking / car booking events.
 */
import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { FaTimes, FaExternalLinkAlt, FaEdit, FaPlane, FaCar } from 'react-icons/fa';
import { getProjectRef } from 'components/app/crm/shared/calendarEventConfig';

const Row = ({ label, children }) => (
  <div className="tw-flex tw-items-start tw-gap-3">
    <span className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wide tw-w-16 tw-shrink-0 tw-pt-0.5">
      {label}
    </span>
    <div className="tw-flex-1 tw-min-w-0">{children}</div>
  </div>
);

Row.propTypes = {
  label:    PropTypes.node,
  children: PropTypes.node,
};

const Badge = ({ children, color }) => (
  <span
    className="tw-inline-block tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-xl tw-border"
    style={{ color, borderColor: `${color}40`, backgroundColor: `${color}12` }}
  >
    {children}
  </span>
);

Badge.propTypes = {
  children: PropTypes.node,
  color:    PropTypes.string,
};

const CalendarEventDetailModal = ({ event, onClose, onEdit }) => {
  if (!event) return null;

  const color   = event.color || '#3b82f6';
  const isDone  = event.status === 'done';
  const isOverdue = !isDone && event.start && dayjs(event.start).isBefore(dayjs());
  const isBooking = event.type === 'travel' || event.type === 'car';
  const isCrmEvent = !isBooking && event.source_type === 'audit';
  // Priority project identifier: job → quote → PCV.
  const projectRef = getProjectRef(event);

  const bookingPath = isBooking
    ? (event.type === 'travel' ? `/booking/travel/${event.bookingId}` : `/booking/car/${event.bookingId}`)
    : null;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-end sm:tw-items-center tw-justify-center tw-p-4 tw-bg-black/40 tw-backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-sm tw-overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Color strip */}
        <div className="tw-h-1.5 tw-w-full" style={{ backgroundColor: color }} />

        {/* Title row */}
        <div className="tw-px-5 tw-pt-4 tw-pb-3 tw-flex tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5 tw-flex-wrap">
              <span className="tw-w-3 tw-h-3 tw-rounded-xl tw-shrink-0" style={{ backgroundColor: color }} />
              <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400">
                {(event.type || '').replace(/_/g, ' ')}
              </span>
              {projectRef && (
                <span className="tw-text-[11px] tw-font-bold tw-font-mono tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-slate-100 tw-text-slate-700">
                  {projectRef}
                </span>
              )}
              {isDone    && <Badge color="#64748b">Done</Badge>}
              {isOverdue && <Badge color="#ef4444">Overdue</Badge>}
            </div>
            <h3 className="tw-text-[15px] tw-font-bold tw-text-slate-900 tw-leading-snug tw-mb-0">
              {event.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tw-w-7 tw-h-7 tw-rounded-xl tw-bg-slate-100 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-text-slate-500 tw-shrink-0 tw-mt-0.5 tw-transition-colors"
          >
            <FaTimes size={11} />
          </button>
        </div>

        <div className="tw-h-px tw-bg-slate-100 tw-mx-5" />

        {/* Details */}
        <div className="tw-px-5 tw-py-4 tw-flex tw-flex-col tw-gap-3">

          {/* Date/time */}
          {event.start && (
            <Row label="When">
              <p className="tw-text-[13px] tw-text-slate-700 tw-font-medium tw-mb-0">
                {dayjs(event.start).format('ddd, DD MMM YYYY')}
              </p>
              <p className="tw-text-[12px] tw-text-slate-400 tw-mb-0">
                {dayjs(event.start).format('HH:mm')}
                {event.end && ` → ${dayjs(event.end).format('HH:mm')}`}
              </p>
            </Row>
          )}

          {/* Booking-specific */}
          {isBooking && event.bookingNumber && (
            <Row label="Booking#">
              <span className="tw-text-[13px] tw-font-mono tw-font-semibold tw-text-slate-700">
                {event.bookingNumber}
              </span>
            </Row>
          )}
          {isBooking && event.note && (
            <Row label={event.type === 'travel' ? 'Destination' : 'Route'}>
              <p className="tw-text-[13px] tw-text-slate-600 tw-leading-snug tw-mb-0">{event.note}</p>
            </Row>
          )}
          {isBooking && event.statusName && (
            <Row label="Status">
              <span className="tw-text-[13px] tw-text-slate-600">{event.statusName}</span>
            </Row>
          )}

          {/* CRM audit event — project numbers */}
          {isCrmEvent && (event.pcvNumber || event.qNumber || event.jNumber) && (
            <Row label="Project">
              <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                {event.jNumber && (
                  <span className="tw-text-[11px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-red-50 tw-text-red-700">
                    {event.jNumber}
                  </span>
                )}
                {event.qNumber && (
                  <span className="tw-text-[11px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-amber-50 tw-text-amber-700">
                    {event.qNumber}
                  </span>
                )}
                {event.pcvNumber && (
                  <span className="tw-text-[11px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-slate-100 tw-text-slate-600">
                    {event.pcvNumber}
                  </span>
                )}
              </div>
            </Row>
          )}

          {/* Activity/task note — may contain TinyMCE HTML */}
          {!isBooking && event.note && (
            <Row label="Note">
              <div
                className="tw-text-[13px] tw-text-slate-600 tw-leading-snug prose-sm"
                dangerouslySetInnerHTML={{ __html: event.note }}
              />
            </Row>
          )}

          {/* Assignee */}
          {event.assignee && (
            <Row label="Assignee">
              <span className="tw-text-[13px] tw-text-slate-700">{event.assignee}</span>
            </Row>
          )}

          {/* Contact */}
          {event.contact && (
            <Row label="Contact">
              <span className="tw-text-[13px] tw-text-slate-700">{event.contact}</span>
            </Row>
          )}

          {/* Link */}
          {(event.resourceUrl || bookingPath) && (
            <Row label="Link">
              <a
                href={bookingPath || event.resourceUrl}
                target="_blank"
                rel="noreferrer"
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[13px] tw-font-medium"
                style={{ color }}
              >
                {isBooking
                  ? <>{event.type === 'travel' ? <FaPlane size={10} /> : <FaCar size={10} />} View Booking</>
                  : <>View project <FaExternalLinkAlt size={9} /></>
                }
              </a>
            </Row>
          )}
        </div>

        {/* Edit action (activities only) */}
        {onEdit && event.editable && !isDone && !isBooking && (
          <div className="tw-px-5 tw-pb-4">
            <div className="tw-h-px tw-bg-slate-100 tw-mb-3" />
            <button
              type="button"
              onClick={() => { onEdit(event); onClose(); }}
              className="tw-w-full tw-py-2 tw-rounded-lg tw-text-sm tw-font-semibold tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1.5"
              style={{ backgroundColor: color }}
            >
              <FaEdit size={12} /> Edit Activity
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

CalendarEventDetailModal.propTypes = {
  event:   PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit:  PropTypes.func,
};

export default CalendarEventDetailModal;
